export type WorkflowChoice = { id: string; label: string };

export type WorkflowConfirmationInput = {
	label: string;
	placeholder?: string;
	required?: boolean;
};

export type WorkflowConfirmation = {
	rule: string;
	message: string;
	choices: WorkflowChoice[];
	required: boolean;
	input?: WorkflowConfirmationInput | null;
};

export type WorkflowAnswer = {
	choice: string;
	input?: string;
};

export type WorkflowLock = {
	locked: boolean;
	message: string;
	remedy: string;
};

export type WorkflowParameterField = {
	key: string;
	type: 'text' | 'boolean' | 'status_list' | 'transitions';
	label: string;
	options?: { value: string; label: string }[];
	statuses?: { value: string; label: string }[];
};

export type WorkflowRuleView = {
	code: string;
	label: string;
	description: string;
	subjects: string[];
	priority: number;
	enabled: boolean;
	parameters: Record<string, unknown>;
	schema: WorkflowParameterField[];
};

type WorkflowUi = {
	ask: (confirmation: WorkflowConfirmation) => Promise<WorkflowAnswer>;
	deny: (message: string, remedy: string) => void;
};

let workflowUi: WorkflowUi | null = null;

export function registerWorkflowUi(ui: WorkflowUi): void {
	workflowUi = ui;
}

/** L'utilisateur a choisi de revenir à la saisie : pas de nouvel envoi. */
export class WorkflowAbortedError extends Error {
	constructor() {
		super('Enregistrement annulé.');
		this.name = 'WorkflowAbortedError';
	}
}

type Refetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Intercepte un 409 de workflow : dialogue de refus, ou questions obligatoires puis nouvel envoi.
 * Retourne null si la réponse n'est pas un conflit de workflow.
 */
export async function resolveWorkflowConflict(
	input: RequestInfo,
	init: RequestInit | undefined,
	response: Response,
	refetch: Refetch
): Promise<Response | null> {
	const contentType = response.headers.get('content-type') ?? '';
	if (!contentType.includes('application/json')) {
		return null;
	}

	let body: {
		code?: string;
		message?: string;
		remedy?: string;
		confirmations?: WorkflowConfirmation[];
	};
	try {
		body = await response.json();
	} catch {
		return null;
	}

	if (body.code === 'workflow_denied') {
		workflowUi?.deny(
			body.message ?? 'Opération refusée par une règle de workflow.',
			body.remedy ?? ''
		);
		throw new Error(
			body.message ?? 'Opération refusée par une règle de workflow.'
		);
	}

	if (body.code !== 'workflow_confirmation_required') {
		return null;
	}

	if (!workflowUi) {
		throw new Error('Confirmation de workflow sans interface.');
	}

	const decisions: Record<string, string> = {};
	const inputs: Record<string, string> = {};
	for (const confirmation of body.confirmations ?? []) {
		const answer = await workflowUi.ask(confirmation);
		if (answer.choice === 'cancel') {
			throw new WorkflowAbortedError();
		}
		decisions[confirmation.rule] = answer.choice;
		if (answer.input) {
			inputs[confirmation.rule] = answer.input;
		}
	}

	let original: Record<string, unknown> = {};
	if (typeof init?.body === 'string' && init.body !== '') {
		original = JSON.parse(init.body) as Record<string, unknown>;
	}

	const previous =
		typeof original.workflowDecisions === 'object' && original.workflowDecisions
			? (original.workflowDecisions as Record<string, string>)
			: {};
	const previousInputs =
		typeof original.workflowInputs === 'object' && original.workflowInputs
			? (original.workflowInputs as Record<string, string>)
			: {};

	return refetch(input, {
		...init,
		body: JSON.stringify({
			...original,
			workflowDecisions: { ...previous, ...decisions },
			workflowInputs: { ...previousInputs, ...inputs },
		}),
	});
}
